import type {
  EvaluateExpressionFieldResult,
  ExpressionFormValues,
  ExpressionVariablesValues,
  ExpressionVariableValue,
} from "./expression-field.type";
import type {
  SchemaExpressionNode,
  SchemaFieldExpression,
} from "@/entities/schema";

import {
  getVariablesNodeFromAST,
  isExpressionError,
  safeEvaluateExpression,
} from "@stelar/expression-engine";

import { isValidNumber } from "@/utilities/number";
import { isValidObject } from "@/utilities/object";

export const evaluateExpressionField = (
  schema: SchemaFieldExpression,
  formValues?: unknown
): EvaluateExpressionFieldResult => {
  if (!isValidFormValues(formValues)) {
    return {};
  }

  const { expression, precision } = schema;

  try {
    const values = getExpressionValues(expression, formValues);
    const result = safeEvaluateExpression(expression, values, { precision });
    return { result };
  } catch (error) {
    if (isExpressionError(error) && error.code === "MISSING_VALUES") {
      // Don't return error if the expression cannot be evaluated because of missing values.
      return {};
    }
    return { error };
  }
};

export const getExpressionValues = (
  expression: SchemaExpressionNode,
  formValues: ExpressionFormValues
) => {
  const values: ExpressionVariablesValues = {};
  const formModels = Object.keys(formValues);

  getExpressionModels(expression).forEach(({ model, variable }) => {
    const value = formValues[model];
    if (isValidFormValue(value)) values[model] = value;
    if (!formModels.includes(model)) {
      throw new Error(`Missing field ${model} for variable ${variable}`);
    }
  });

  return values;
};

/**
 * Extracts the variable models referenced by an expression AST, returning an
 * empty list if the AST cannot be parsed.
 */
const getExpressionModels = (expression: SchemaExpressionNode) => {
  try {
    return getVariablesNodeFromAST(expression);
  } catch {
    return [];
  }
};

const isValidFormValues = (
  formValues?: unknown
): formValues is ExpressionFormValues => {
  return isValidObject(formValues);
};

const isValidFormValue = (
  formValue?: unknown
): formValue is ExpressionVariableValue => {
  if (isValidNumber(formValue)) return true;
  if (typeof formValue === "string" && formValue !== "") return true;
  return false;
};
